import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { getCurrentUser } from './services/apiclient';
import { factory } from 'typescript';
import axiosInstance from './services/apiinterseptor';
import { toast } from 'sonner';
import { isAiFunnelOnlyUser } from './utils/aiFunnelUser';

export type UserRole = 'ADMIN' | 'FBDE' | 'FBDE_ADMIN' | 'CONSULTANT' | 'MODERATOR' | 'SHM' | 'SHM_ADMIN' | 'AGENCY_ADMIN' | 'BDE' | 'HM' | 'AGENCY' | 'HR' | 'TPO'| 'INVESTOR' | 'COMPANY' | 'FREELANCE_COUNSELLOR' | 'MASTER_BDE' | 'FREELANCE_BDE' | 'BDE_INTERN' | 'INTERNAL_TEAM'; 



export interface Permission {
  dashboard: boolean;
  activeUsers: boolean;
  publicSubjobApplications: boolean;
  externalJobApplications: boolean;
  approchesView: boolean;
  hotLeadsView: boolean;
  campusDriveApplications: boolean;
  createJob: boolean;
  createSubJob: boolean;
  companies: boolean;
  viewApplications: boolean;
  viewJobCandidates: boolean; // Separate permission for viewing candidates for a specific job
  viewCandidates: boolean;
  addCandidates: boolean;
  editJob: boolean;
  deleteJob: boolean;
  notifications: boolean;
  joinedCandidates: boolean;
  myHiringManagers: boolean;
  assessments: boolean;
  users: boolean;
  ViewSubJobs: boolean;
  PayoutCandidates: boolean;
  TenureCandidates: boolean;
  RecruiterOnboarding: boolean;
  TalentPool: boolean;
  tpoFeatures: boolean;
  transactions: boolean;
  leaderBoard: boolean;
  candidatesOfAssessment: boolean;
  aiMode: boolean;
  TpoView: boolean;
  collegeReferralProgram: boolean;
  companyReferralProgram: boolean;
  candidateDatabase: boolean;
  exam: boolean;
  assignedJobs?: boolean;
  atsChecker?: boolean;
  complaints: boolean;
  manageComplaints: boolean;
  manageBlockedCandidates: boolean;
  coordinations?: boolean;
  highlights?: boolean;
  companyApproaches?: boolean;
  aiCallAgent?: boolean;
  publicCompanyApplications?: boolean;
  recruitmentMahakumb?: boolean;
  investorDashboard: boolean;
  hunarFunnelView?: boolean;
  hunarAllocationManage?: boolean;
  hotline?: boolean;
  liveSheets?: boolean;
}

const rolePermissions: Record<UserRole, Permission> = {
  ADMIN: {
    aiMode: true,
    dashboard: true,
    activeUsers: true,
    publicSubjobApplications: true,
    externalJobApplications: true,
    approchesView: true,
    hotLeadsView: true,
    manageBlockedCandidates: true,
    campusDriveApplications: true,
    createJob: true,
    createSubJob: false,
    companies: true,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    viewCandidates: true,
    addCandidates: true,
    editJob: true,
    deleteJob: true,
    notifications: true,
    users: true,
    myHiringManagers: false,
    assessments: true,
    ViewSubJobs: true,
    PayoutCandidates: true,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    TalentPool: true,
    leaderBoard: true,
    transactions: true,
    candidatesOfAssessment: true,
    TpoView: true,
    tpoFeatures: true,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: true,
    assignedJobs: true,
    atsChecker: true,
    complaints: true,
    manageComplaints: true,
    candidateDatabase: true,
    coordinations: true,
    highlights: true,
    companyApproaches:true,
    aiCallAgent: true,
    publicCompanyApplications: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
    hunarFunnelView: true,
    hunarAllocationManage: true,
    hotline: true,
    liveSheets: true,
  },
  FREELANCE_COUNSELLOR: {
    activeUsers: false,
    aiMode: true,
    dashboard: true,
    publicSubjobApplications: false,
    externalJobApplications: false,
    approchesView: false,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: true,
    createJob: false,
    createSubJob: false,
    companies: false,
    viewApplications: false,
    viewJobCandidates: false,
    joinedCandidates: false,
    viewCandidates: false,
    addCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: false,
    assessments: true,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    TalentPool: true,
    leaderBoard: false,
    transactions: true,
    candidatesOfAssessment: true,
    TpoView: true,
    tpoFeatures: false,
    collegeReferralProgram: false,
    companyReferralProgram: false,
    exam: false,
    complaints: false,
    manageComplaints: false,
    candidateDatabase: false,
    highlights: false,
    recruitmentMahakumb: false,
    investorDashboard: false,
  },
  FBDE: {
    aiMode: true,
    dashboard: true,
    activeUsers: true,
    publicSubjobApplications: true,
    externalJobApplications: false,
    approchesView: true,
    hotLeadsView: true,
    manageBlockedCandidates: false,
    campusDriveApplications: true,
    createJob: true,
    createSubJob: true,
    companies: true,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    viewCandidates: false,
    addCandidates: true,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: true,
    assessments: true,
    ViewSubJobs: true,
    PayoutCandidates: false,
    TenureCandidates: true,
    RecruiterOnboarding: true,
    TalentPool: true,
    tpoFeatures: false,
    transactions: true,
    candidatesOfAssessment: true,
    leaderBoard: false,
    TpoView: true,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: true,
    complaints: true,
    manageComplaints: false,
    candidateDatabase: true,
    coordinations: false,
    highlights: true,
    companyApproaches: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
    hotline: true,
  },
  CONSULTANT: {
    aiMode: true,
    dashboard: true,
    activeUsers: true,
    publicSubjobApplications: true,
    externalJobApplications: false,
    approchesView: true,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: true,
    createJob: false,
    createSubJob: true,
    companies: true,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    viewCandidates: false,
    addCandidates: true,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: true,
    assessments: true,
    ViewSubJobs: true,
    PayoutCandidates: false,
    TenureCandidates: true,
    RecruiterOnboarding: true,
    TalentPool: true,
    tpoFeatures: false,
    transactions: true,
    candidatesOfAssessment: true,
    leaderBoard: false,
    TpoView: true,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: true,
    complaints: true,
    manageComplaints: false,
    candidateDatabase: true,
    coordinations: false,
    recruitmentMahakumb: false,
    investorDashboard: false,
  },
  SHM: {
    aiMode: true,
    dashboard: true,
    activeUsers: true,
    publicSubjobApplications: true,
    externalJobApplications: true,
    approchesView: true,
    hotLeadsView: true,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: false,
    createSubJob: true,
    companies: false,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    addCandidates: true,
    viewCandidates: false,
    editJob: true,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: true,
    assessments: false,
    ViewSubJobs: true,
    PayoutCandidates: false,
    TenureCandidates: true,
    RecruiterOnboarding: true,
    TalentPool: true,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: true,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: false,
    manageComplaints: false,
    atsChecker: true,
    complaints: true,
    candidateDatabase: true,
    coordinations: true,
    highlights: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
    hotline: true,
  },
  SHM_ADMIN: {
    aiMode: true,
    dashboard: true,
    activeUsers: true,
    publicSubjobApplications: true,
    externalJobApplications: true,
    approchesView: true,
    hotLeadsView: true,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: false,
    createSubJob: true,
    companies: false,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    addCandidates: true,
    viewCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: true,
    assessments: false,
    ViewSubJobs: true,
    PayoutCandidates: false,
    TenureCandidates: true,
    RecruiterOnboarding: true,
    TalentPool: true,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: true,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: false,
    manageComplaints: false,
    atsChecker: true,
    complaints: true,
    candidateDatabase: true,
    coordinations: true,
    highlights: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
    hotline: true,
  },
  AGENCY_ADMIN: {
    aiMode: true,
    dashboard: true,
    activeUsers: true,
    publicSubjobApplications: true,
    externalJobApplications: true,
    approchesView: true,
    hotLeadsView: true,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: false,
    createSubJob: true,
    companies: false,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    addCandidates: true,
    viewCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: true,
    assessments: false,
    ViewSubJobs: true,
    PayoutCandidates: false,
    TenureCandidates: true,
    RecruiterOnboarding: true,
    TalentPool: true,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: true,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: false,
    manageComplaints: false,
    atsChecker: true,
    complaints: true,
    candidateDatabase: true,
    coordinations: true,
    highlights: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
  },
  FBDE_ADMIN: {
    aiMode: true,
    dashboard: true,
    activeUsers: true,
    publicSubjobApplications: true,
    externalJobApplications: true,
    approchesView: true,
    hotLeadsView: true,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: false,
    createSubJob: true,
    companies: false,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    addCandidates: true,
    viewCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: true,
    assessments: false,
    ViewSubJobs: true,
    PayoutCandidates: false,
    TenureCandidates: true,
    RecruiterOnboarding: true,
    TalentPool: true,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: true,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: false,
    manageComplaints: false,
    atsChecker: true,
    complaints: true,
    candidateDatabase: true,
    coordinations: true,
    highlights: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
    hotline: true,
  },
  HM: {
    aiMode: true,
    dashboard: true,
    activeUsers: true,
    publicSubjobApplications: true,
    externalJobApplications: false,
    approchesView: true,
    hotLeadsView: true,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: false,
    createSubJob: true,
    companies: false,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    addCandidates: true,
    viewCandidates: false,
    editJob: true,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: true,
    assessments: false,
    ViewSubJobs: true,
    PayoutCandidates: false,
    TenureCandidates: true,
    RecruiterOnboarding: true,
    TalentPool: true,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: false,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: true,
    coordinations: true,
    highlights: true,
    atsChecker: true,
    assignedJobs: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
    hotline: true,
  },
  AGENCY: {
    aiMode: true,
    dashboard: true,
    activeUsers: true,
    publicSubjobApplications: true,
    externalJobApplications: false,
    approchesView: true,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: false,
    createSubJob: true,
    companies: false,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    addCandidates: true,
    viewCandidates: false,
    editJob: true,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: true,
    assessments: false,
    ViewSubJobs: true,
    PayoutCandidates: false,
    TenureCandidates: true,
    RecruiterOnboarding: true,
    TalentPool: true,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: false,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: true,
    coordinations: true,
    highlights: true,
    atsChecker: true,
    assignedJobs: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
    hotline: true,
  },
  HR: {
    aiMode:true,
    dashboard: true,
    publicSubjobApplications:true,
    externalJobApplications: false,
    approchesView: true,
    hotLeadsView: true,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: false,
    createSubJob: true,
    companies: false,
    viewApplications: true,
    viewJobCandidates: true,
    joinedCandidates: true,
    addCandidates: true,
    viewCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: true,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: true,
    TalentPool: true,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: false,
    assignedJobs: true,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: true,
    activeUsers:false,
    coordinations: false,
    highlights: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
    hotline: true,
  },
  COMPANY: {
    aiMode: true,
    dashboard: true,
    activeUsers: false,
    publicSubjobApplications: false,
    externalJobApplications: false,
    approchesView: false,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: true,
    createSubJob: false,
    companies: false,
    viewApplications: false, // General applications view (for company dashboard)
    viewJobCandidates: true, // Separate permission for viewing candidates for a specific job
    joinedCandidates: false,
    viewCandidates: false,
    addCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    TalentPool: false,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: false,
    companyReferralProgram: false,
    exam: false,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: true,
    coordinations: false,
    highlights: false,
    recruitmentMahakumb: false,
    investorDashboard: false,
  },

  BDE: {
    aiMode: true,
    dashboard: true,
    activeUsers: false,
    publicSubjobApplications: true,
    externalJobApplications: false,
    approchesView: true,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: true,
    createSubJob: false,
    companies: true,
    viewApplications: false,
    viewJobCandidates: false,
    joinedCandidates: false,
    addCandidates: false,
    viewCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    TalentPool: false,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: false,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: true,
    coordinations: false,
    highlights: false,
    investorDashboard: false,
  },

  TPO: {
    aiMode: true,
    TalentPool: true,
    tpoFeatures:true,
    dashboard: true,
    publicSubjobApplications:false,
    externalJobApplications: false,
    approchesView: false,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: true,
    createJob: false,
    createSubJob: false,
    companies: false,
    viewApplications: false,
    viewJobCandidates: false,
    joinedCandidates: false,
    viewCandidates: false,
    addCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    activeUsers: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: false,
    companyReferralProgram: false,
    exam: false,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: false,
    coordinations: false,
    highlights: false,
    recruitmentMahakumb: false,
    investorDashboard: false,
  },
  INVESTOR: {
    aiMode: false,
    dashboard: true,
    activeUsers: false,
    publicSubjobApplications: false,
    externalJobApplications: false,
    approchesView: false,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: false,
    createSubJob: false,
    companies: false,
    viewApplications: true,
    viewJobCandidates: false,
    joinedCandidates: true,
    viewCandidates: false,
    addCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    TalentPool: false,
    tpoFeatures: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: false,
    companyReferralProgram: false,
    candidateDatabase: false,
    exam: false,
    complaints: true,
    manageComplaints: false,
    coordinations: false,
    highlights: false,
    companyApproaches: false,
    aiCallAgent: false,
    publicCompanyApplications: false,
    recruitmentMahakumb: false,
    investorDashboard: true,
  },
  MASTER_BDE: {
    aiMode: true,
    TalentPool: false,
    tpoFeatures:false,
    dashboard: true,
    publicSubjobApplications:false,
    externalJobApplications: false,
    approchesView: false,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: true,
    createSubJob: false,
    companies: true,
    viewApplications: false,
    viewJobCandidates: true,
    joinedCandidates: false,
    viewCandidates: false,
    addCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    activeUsers: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: true,
    collegeReferralProgram: true,
    exam: false,
    companyReferralProgram: true,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: false,
    coordinations: false,
    highlights: false,
    companyApproaches: true,
    recruitmentMahakumb: false,
    publicCompanyApplications: true,
    investorDashboard: false,
  },
  FREELANCE_BDE: {
    aiMode: true,
    TalentPool: false,
    tpoFeatures:false,
    dashboard: true,
    publicSubjobApplications:false,
    externalJobApplications: false,
    approchesView: false,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: true,
    createSubJob: false,
    companies: true,
    viewApplications: false,
    viewJobCandidates: false,
    joinedCandidates: false,
    viewCandidates: false,
    addCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    activeUsers: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: true,
    exam: false,
    companyReferralProgram: true,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: false,
    coordinations: false,
    highlights: false,
    companyApproaches: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
  },
  BDE_INTERN: {
    aiMode: true,
    TalentPool: false,
    tpoFeatures:false,
    dashboard: true,
    publicSubjobApplications:false,
    externalJobApplications: false,
    approchesView: false,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: true,
    createSubJob: false,
    companies: true,
    viewApplications: false,
    viewJobCandidates: false,
    joinedCandidates: false,
    viewCandidates: false,
    addCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    activeUsers: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: true,
    exam: false,
    companyReferralProgram: true,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: false,
    coordinations: false,
    highlights: false,
    companyApproaches: true,
    recruitmentMahakumb: false,
    investorDashboard: false,
  },
  MODERATOR: {
    aiMode: false,
    TalentPool: false,
    tpoFeatures: false,
    dashboard: true,
    publicSubjobApplications: false,
    externalJobApplications: false,
    approchesView: false,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: true,
    createSubJob: false,
    companies: true,
    viewApplications: false,
    viewJobCandidates: false,
    joinedCandidates: false,
    viewCandidates: false, // View Candidates (candidate database) - separate from public company applications
    addCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    activeUsers: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    transactions: false,
    candidatesOfAssessment: false,
    leaderBoard: false,
    TpoView: false,
    collegeReferralProgram: false,
    exam: false,
    companyReferralProgram: false,
    manageComplaints: false,
    complaints: true,
    candidateDatabase: false,
    coordinations: false,
    highlights: false,
    companyApproaches: false,
    publicCompanyApplications: true, // Public Company Applications - separate permission
    recruitmentMahakumb: false,
    investorDashboard: false,
  },
  INTERNAL_TEAM: {
    aiMode: false,
    dashboard: true,
    activeUsers: false,
    publicSubjobApplications: false,
    externalJobApplications: false,
    approchesView: false,
    hotLeadsView: false,
    manageBlockedCandidates: false,
    campusDriveApplications: false,
    createJob: false,
    createSubJob: false,
    companies: false,
    viewApplications: false,
    viewJobCandidates: false,
    joinedCandidates: false,
    viewCandidates: false,
    addCandidates: false,
    editJob: false,
    deleteJob: false,
    notifications: false,
    users: false,
    myHiringManagers: false,
    assessments: false,
    ViewSubJobs: false,
    PayoutCandidates: false,
    TenureCandidates: false,
    RecruiterOnboarding: false,
    TalentPool: false,
    tpoFeatures: false,
    transactions: false,
    leaderBoard: false,
    candidatesOfAssessment: false,
    TpoView: false,
    collegeReferralProgram: true,
    companyReferralProgram: true,
    exam: false,
    assignedJobs: false,
    atsChecker: false,
    complaints: false,
    manageComplaints: false,
    candidateDatabase: false,
    coordinations: false,
    highlights: false,
    companyApproaches: false,
    aiCallAgent: false,
    publicCompanyApplications: false,
    recruitmentMahakumb: false, // Only permission for internal team
    investorDashboard: false,
  }
};

interface User {
  id: string;
  name: string;
  username?: string;
  email: string;
  userId?: string;
  role: UserRole;
  isOnboarded?: boolean;
  hasHunarFunnelAllocation?: boolean;
  isMahkumbnominationSubmitted?: boolean;
  linkedinUrl?: string;
  profilePhotoUrl?: string;
  /** ADMIN-gated; HM may create HR under self when true. */
  canCreateUsers?: boolean;
  /** ADMIN-gated; SHM/HM/SHM_ADMIN/FBDE_ADMIN may create/edit jobs pending ADMIN approval. */
  canPostJobs?: boolean;
}

interface RbacContextProps {
  user: any | null;
  role: UserRole | null;
  hasPermission: (permission: keyof Permission) => boolean;
  login: (userData: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  userId?: string;
  setUser: React.Dispatch<React.SetStateAction<User | null>>; // added
}

const RbacContext = createContext<RbacContextProps | undefined>(undefined);

export const RbacProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);

  // Handle token and user from URL (redirect from another portal)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash?.replace(/^#/, '') || '');
    const token = params.get('token') ?? params.get('Token') ?? params.get('accessToken') ?? hashParams.get('token') ?? hashParams.get('Token') ?? hashParams.get('accessToken');
    const userParam = params.get('user') ?? hashParams.get('user');

    if (token) {
      localStorage.setItem('accessToken', token);
      axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;

      if (userParam) {
        try {
          const userData = JSON.parse(userParam);
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (e) {
          console.warn('Failed to parse user from URL:', e);
        }
      } else {
        // Token only: fetch user from API
        getCurrentUser()
          .then((res: any) => {
            const userData = res?.data?.user ?? res?.user;
            if (userData) {
              setUser(userData);
              localStorage.setItem('user', JSON.stringify(userData));
            }
          })
          .catch(() => {});
      }

      // Clean URL (remove token and user from address bar for security)
      const cleanUrl = window.location.pathname + (window.location.hash || '');
      window.history.replaceState({}, document.title, cleanUrl);

      toast.success('Login successful! Welcome to the portal.');
    }
  }, []);

  // Load user from localStorage on app initialization
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    console.log("Stored User:", storedUser);
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        localStorage.removeItem("user");
      }
    }
  }, []);

  console.log("Rbac User:", user);

  // const hasPermission = (permission: keyof Permission): boolean => {
  //   if (!user?.role) return false;
  //   if(permission==="exam" && user.role==="FBDE" && user.email==="mohali@earlyjobs.in") return true
  //   if(permission==="exam" && user.role==="FBDE" && user.email!=="mohali@earlyjobs.in") return false
  //   return rolePermissions[user.role]?.[permission] ?? false;
  // };

  const hasPermission = (permission: keyof Permission): boolean => {
    if (!user?.role) return false;
    if (permission === "liveSheets") return user.role === "ADMIN";
    if (permission === "hunarFunnelView") {
      return user.role === "ADMIN" || Boolean(user.hasHunarFunnelAllocation) || isAiFunnelOnlyUser(user);
    }
    if (permission === "hunarAllocationManage") {
      return user.role === "ADMIN" || Boolean(user.hasHunarFunnelAllocation);
    }
    if (
      permission === "exam" &&
      (user.role === "FBDE" || user.role === "CONSULTANT" || user.role === "ADMIN") &&
      (user.email === "mohali@earlyjobs.in" ||
        user.email === "hyderabad@earlyjobs.in")
    )
      return true;
    if (
      permission === "exam" &&
      (user.role === "FBDE" || user.role === "CONSULTANT" || user.role === "ADMIN") &&
      user.email !== "mohali@earlyjobs.in" &&
      user.email !== "hyderabad@earlyjobs.in"
    )
      return false;
    const role = user.role as UserRole;
    if (role in rolePermissions) {
      return rolePermissions[role]?.[permission] ?? false;
    }
    return false;
  };

  const login = (userData: User) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  };

  const value: RbacContextProps = {
    user,
    role: user?.role || null,
    hasPermission,
    login,
    logout,
    isAuthenticated: !!user,
    setUser, // added here
  };

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
};

export const useRbac = () => {
  const context = useContext(RbacContext);

  if (!context) {
    throw new Error("useRbac must be used within a RbacProvider");
  }
  return context;
};

export const usePermission = (permission: keyof Permission) => {
  const { hasPermission, user, role, setUser } = useRbac();
  return {
    allowed: hasPermission(permission),
    user,
    role,
    setUser,
  };
};
